'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTable } from '@tanstack/react-table';
import { useState } from 'react';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { VAULT_TABLE_5_ROWS_HEIGHT } from '@/components/VaultPage/config';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { PAGE_SIZE, vaultAllocationColumnsDefs } from './config';
import { vaultReallocationTableFeatures } from './config/VaultReallocation.columns';
import useTableReallocateRow from './hooks/useTableReallocateRow';
import { reallocationsQueryOptions } from './reallocations.query';
export interface TableReallocateClientProps {
  address: Address;
  chainId: ChainId;
}
const TableReallocateClient = t0 => {
  const $ = _c(67);
  const {
    address,
    chainId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [undefined];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cursors, setCursors] = useState(t1);
  const pageIndex = cursors.length - 1;
  const t2 = cursors[pageIndex];
  let t3;
  if ($[1] !== address || $[2] !== chainId || $[3] !== t2) {
    t3 = reallocationsQueryOptions({
      chainId,
      address,
      cursor: t2
    });
    $[1] = address;
    $[2] = chainId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      ...t3,
      placeholderData: keepPreviousData
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data,
    isPlaceholderData,
    isFetching,
    error
  } = useQuery(t4);
  const queryClient = useQueryClient();
  const [navigating, setNavigating] = useState(false);
  const loading = !data || isPlaceholderData && isFetching || navigating;
  const showError = error !== null && !data;
  let t5;
  if ($[7] !== address || $[8] !== chainId || $[9] !== navigating || $[10] !== queryClient) {
    t5 = target => {
      if (navigating) {
        return;
      }
      setNavigating(true);
      queryClient.fetchQuery(reallocationsQueryOptions({
        chainId,
        address,
        cursor: target.at(-1)
      })).then(() => setCursors(target)).catch(_temp).finally(() => setNavigating(false));
    };
    $[7] = address;
    $[8] = chainId;
    $[9] = navigating;
    $[10] = queryClient;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const goTo = t5;
  const nextCursor = isPlaceholderData ? null : data?.nextCursor ?? null;
  let t6;
  if ($[12] !== data?.items) {
    t6 = data?.items ?? [];
    $[12] = data?.items;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== chainId || $[15] !== t6) {
    t7 = {
      items: t6,
      chainId
    };
    $[14] = chainId;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const formattedData = useTableReallocateRow(t7);
  let t8;
  if ($[17] !== formattedData) {
    t8 = {
      features: vaultReallocationTableFeatures,
      data: formattedData,
      manualSorting: false,
      manualPagination: true,
      columns: vaultAllocationColumnsDefs,
      enableSortingRemoval: false,
      sortDescFirst: false
    };
    $[17] = formattedData;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const vaultAllocationTable = useTable(t8);
  let t9;
  if ($[19] !== loading || $[20] !== showError || $[21] !== vaultAllocationTable) {
    t9 = !loading && !showError && vaultAllocationTable.getRowModel().rows.length === 0;
    $[19] = loading;
    $[20] = showError;
    $[21] = vaultAllocationTable;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const emptyRows = t9;
  const t10 = data?.countTotal;
  let t11;
  if ($[23] !== pageIndex) {
    t11 = {
      pageIndex,
      pageSize: PAGE_SIZE
    };
    $[23] = pageIndex;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = {
      first: PAGE_SIZE,
      totalCount: t10,
      pagination: t11,
      columnsDefs: vaultAllocationColumnsDefs
    };
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const skeletonRows = useSkeletonRows(t12);
  let t13;
  if ($[28] !== emptyRows || $[29] !== loading || $[30] !== showError || $[31] !== skeletonRows || $[32] !== vaultAllocationTable) {
    t13 = !emptyRows && !showError && <Table.Table tableVariant="compact"><Table.Thead>{vaultAllocationTable.getHeaderGroups().map(_temp2)}</Table.Thead><Table.Tbody>{!loading && vaultAllocationTable.getRowModel().rows.map(_temp3)}{loading && skeletonRows.map(_temp4)}</Table.Tbody></Table.Table>;
    $[28] = emptyRows;
    $[29] = loading;
    $[30] = showError;
    $[31] = skeletonRows;
    $[32] = vaultAllocationTable;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== emptyRows) {
    t14 = emptyRows && <TableNoResults text="No allocation history found." height={VAULT_TABLE_5_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[34] = emptyRows;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== showError) {
    t15 = showError && <ErrorDataUnavailable minHeight={VAULT_TABLE_5_ROWS_HEIGHT} style={{
      borderRadius: "0"
    }} />;
    $[36] = showError;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t16 = <TableScrollArea className="grid h-full w-full">{t13}{t14}{t15}</TableScrollArea>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== data?.partial || $[43] !== loading) {
    t17 = data?.partial && !loading && <p className="text-secondary-foreground px-sm text-xs" data-testid="vault-reallocations-partial">Some reallocations could not be loaded, so this page may be missing rows.</p>;
    $[42] = data?.partial;
    $[43] = loading;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const t18 = showError ? error : null;
  let t19;
  if ($[45] !== data) {
    t19 = isDefined(data?.countTotal) ? Math.ceil(data.countTotal / PAGE_SIZE) : undefined;
    $[45] = data;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = pageIndex > 0;
  const t21 = nextCursor !== null;
  let t22;
  if ($[47] !== cursors || $[48] !== goTo) {
    t22 = () => goTo(cursors.slice(0, -1));
    $[47] = cursors;
    $[48] = goTo;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== cursors || $[51] !== goTo || $[52] !== nextCursor) {
    t23 = () => {
      if (nextCursor !== null) {
        goTo([...cursors, nextCursor]);
      }
    };
    $[50] = cursors;
    $[51] = goTo;
    $[52] = nextCursor;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== loading || $[55] !== pageIndex || $[56] !== t18 || $[57] !== t19 || $[58] !== t20 || $[59] !== t21 || $[60] !== t22 || $[61] !== t23) {
    t24 = <CursorPagination loading={loading} error={t18} pageIndex={pageIndex} pageCount={t19} canPreviousPage={t20} canNextPage={t21} previousPage={t22} nextPage={t23} />;
    $[54] = loading;
    $[55] = pageIndex;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t16 || $[64] !== t17 || $[65] !== t24) {
    t25 = <div className="gap-xs flex h-full w-full flex-col" data-testid="vault-reallocations">{t16}{t17}{t24}</div>;
    $[63] = t16;
    $[64] = t17;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  return t25;
};
export default TableReallocateClient;
function _temp() {}
function _temp2(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp3(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp4(row_0, index) {
  return <TrBodyRow key={`skeleton-table-reallocate-${index}`} skeletonRow={row_0} />;
}